import { useState, useCallback, useRef, useEffect } from 'react';
import { useOrderStore, useResourceStore } from '@/store';
import { RushOrder } from '@/types';
import { DelayReviewDialog, ReviewCompleteDialog } from '@/components/Dialog/DelayReviewDialog';
import {
  calculateConflicts,
  createDelayDecision,
  recalculateConflicts,
} from '@/services/conflictCalculator';
import { ConflictResult, DelayDecision } from '@/types/order';
import { toast } from '@/components/UI/Toast';

interface RushOrderFormProps {
  onClose: () => void;
}

export function RushOrderForm({ onClose }: RushOrderFormProps) {
  const mainWorkOrders = useOrderStore((state) => state.mainWorkOrders);
  const subWorkOrders = useOrderStore((state) => state.subWorkOrders);
  const addRushOrder = useOrderStore((state) => state.addRushOrder);
  const products = useResourceStore((state) => state.products);
  const persons = useResourceStore((state) => state.persons);
  const machines = useResourceStore((state) => state.machines);
  const materials = useResourceStore((state) => state.materials);

  // Ref for first input (auto-focus)
  const firstInputRef = useRef<HTMLSelectElement>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    productId: '',
    productName: '',
    totalQuantity: 100,
    customer: '',
    batchNo: '',
    orderDate: new Date().toISOString().slice(0, 10),
    originalDeliveryDate: '',
    requestedDeliveryDate: '',
    reason: '',
    priority: '重要' as RushOrder['priority'],
  });

  // 延後審核流程狀態 | Delay review flow state
  const [showReviewDialog, setShowReviewDialog] = useState(false);
  const [showCompleteDialog, setShowCompleteDialog] = useState(false);
  const [conflictResults, setConflictResults] = useState<ConflictResult[]>([]);
  const [currentConflictIndex, setCurrentConflictIndex] = useState(0);
  const [delayDecisions, setDelayDecisions] = useState<DelayDecision[]>([]);
  const [pendingRushOrder, setPendingRushOrder] = useState<RushOrder | null>(null);

  // 範例已載入提示狀態
  const [showExampleLoaded, setShowExampleLoaded] = useState(false);

  // 表單驗證狀態 | Form validation state
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [touched, setTouched] = useState<Set<string>>(new Set());

  // 即時驗證單一欄位 | Real-time validate single field
  const validateField = useCallback((name: string, value: any): string => {
    switch (name) {
      case 'productId':
        if (!value) return '請選擇產品';
        return '';
      case 'customer':
        if (!value.trim()) return '請輸入客戶名稱';
        if (value.trim().length < 2) return '客戶名稱至少需要 2 個字元';
        return '';
      case 'originalDeliveryDate':
        if (!value) return '請選擇原訂交期';
        return '';
      case 'requestedDeliveryDate':
        if (!value) return '請選擇需求交期';
        // 檢查是否早於原訂交期
        if (formData.originalDeliveryDate) {
          const originalDate = new Date(formData.originalDeliveryDate);
          const requestedDate = new Date(value);
          if (requestedDate >= originalDate) return '需求交期必須早於原訂交期';
        }
        return '';
      case 'reason':
        if (!value.trim()) return '請填寫插單原因';
        if (value.trim().length < 10) return '插單原因至少需要 10 個字元';
        return '';
      case 'totalQuantity':
        if (!value || value <= 0) return '數量必須大於 0';
        if (value > 1000000) return '數量不能超過 1,000,000';
        return '';
      default:
        return '';
    }
  }, [formData.originalDeliveryDate]);

  // 處理欄位變更並即時驗證 | Handle field change with real-time validation
  const handleChange = useCallback((name: string, value: any) => {
    setFormData((prev) => ({ ...prev, [name]: value }));

    // 如果該欄位已被觸碰，進行即時驗證 | Real-time validate if touched
    if (touched.has(name)) {
      const error = validateField(name, value);
      setErrors((prev) => {
        const newErrors = { ...prev };
        if (error) {
          newErrors[name] = error;
        } else {
          delete newErrors[name];
        }
        return newErrors;
      });
    } else {
      // 如果未被觸碰，只清除錯誤 | Just clear error if not touched
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  }, [touched, validateField]);

  // 處理欄位失焦 | Handle field blur
  const handleBlur = useCallback((name: string) => {
    setTouched((prev) => new Set([...prev, name]));
    const error = validateField(name, formData[name as keyof typeof formData]);
    setErrors((prev) => {
      const newErrors = { ...prev };
      if (error) {
        newErrors[name] = error;
      } else {
        delete newErrors[name];
      }
      return newErrors;
    });
  }, [formData, validateField]);

  // ESC 鍵關閉對話框 & 自動聚焦
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !showReviewDialog) {
        onClose();
      }
    };

    // Auto-focus first input when dialog opens
    setTimeout(() => firstInputRef.current?.focus(), 100);

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose, showReviewDialog]);

  // 三種範例場景
  const importantScenarios = [
    {
      name: 'VIP 客戶加單',
      productId: 'P001',
      productName: '高精度壓力感測器',
      totalQuantity: 200,
      customer: '長期合作-A科技集團',
      priority: '重要' as RushOrder['priority'],
      originalDeliveryDate: '2026-04-15',
      requestedDeliveryDate: '2026-03-01',
      reason: 'VIP 客戶訂單增量，原有供應鏈穩定。此插單會與現有工單 MW-2026-001-A/MW-2026-001-B 產生人員衝突（E001, E003, E004, E010），建議安排加班或調整班次，以維持長期合作關係。',
    },
    {
      name: '原型機開發',
      productId: 'P004',
      productName: '智慧控制晶片',
      totalQuantity: 80,
      customer: '策略合作-B半導體',
      priority: '重要' as RushOrder['priority'],
      originalDeliveryDate: '2026-05-01',
      requestedDeliveryDate: '2026-03-15',
      reason: '新產品原型機開發，具有策略合作意義。此插單會與現有工單 MW-2026-003 產生機台衝突（M003: 電路測試機、M006: 焊接機），建議調整生產排程，優先排入空檔時段。',
    },
    {
      name: '品質升級專案',
      productId: 'P007',
      productName: '散熱模組',
      totalQuantity: 300,
      customer: '重要客戶-C電子廠',
      priority: '重要' as RushOrder['priority'],
      originalDeliveryDate: '2026-04-20',
      requestedDeliveryDate: '2026-03-10',
      reason: '產品品質升級，具戰略價值。此插單會消耗 MAT012（導熱矽脂），目前庫存 15 盒（安全庫存 5 盒），同時也會與工單 MW-2026-004 產生物料衝突，建議分批採購補充庫存。',
    },
  ];

  const urgentScenarios = [
    {
      name: '生產線緊急維修',
      productId: 'P002',
      productName: '鋁合金外殼',
      totalQuantity: 500,
      customer: '緊急-D維修公司',
      priority: '緊急' as RushOrder['priority'],
      originalDeliveryDate: '2026-04-01',
      requestedDeliveryDate: '2026-02-01',
      reason: '客戶生產線故障，外殼損壞需緊急更換。此插單會與現有工單 MW-2026-002 產生機台衝突（M001: 砂型鑄造機、M002: 精密加工機、M008: 切割機），同時也會產生人員衝突（E002, E005, E008），需要立即安排加班並調整排程。',
    },
    {
      name: '原型機緊急上市',
      productId: 'P003',
      productName: '智慧控制模組',
      totalQuantity: 150,
      customer: '緊急採購-E智慧科技',
      priority: '緊急' as RushOrder['priority'],
      originalDeliveryDate: '2026-04-20',
      requestedDeliveryDate: '2026-02-05',
      reason: '新產品上市計畫提前，物料需求急迫。此插單會消耗 MAT008（陽極藥水），目前庫存僅 8 桶（安全庫存 3 桶），也會與工單 MW-2026-002 產生物料衝突，且涉及 M004/M006 機台衝突，建議緊急採購並加班生產。',
    },
    {
      name: '展會緊急插單',
      productId: 'P005',
      productName: '連接器組',
      totalQuantity: 400,
      customer: '緊急-F貿易公司',
      priority: '緊急' as RushOrder['priority'],
      originalDeliveryDate: '2026-05-10',
      requestedDeliveryDate: '2026-02-15',
      reason: '國際展會展示需求，時間壓力極大。此插單會產生多重衝突：人員（E006, E007, E009）、機台（M002, M005, M007）、物料（MAT009: 鍍層材料，庫存 6 公斤，安全庫存 2 公斤），需要全面調整生產計畫並安排三班輪替生產。',
    },
  ];;

  // 載入隨機範例
  const handleLoadImportantExample = (index: number) => {
    const scenario = importantScenarios[index];

    setFormData({
      ...formData,
      productId: scenario.productId,
      productName: scenario.productName,
      totalQuantity: scenario.totalQuantity,
      customer: scenario.customer,
      batchNo: `EXAMPLE-${Date.now()}`,
      originalDeliveryDate: scenario.originalDeliveryDate,
      requestedDeliveryDate: scenario.requestedDeliveryDate,
      reason: scenario.reason,
      priority: scenario.priority,
    });

    // 顯示載入成功訊息
    setShowExampleLoaded(true);
    setTimeout(() => setShowExampleLoaded(false), 2000);
  };

  const handleLoadUrgentExample = (index: number) => {
    const scenario = urgentScenarios[index];

    setFormData({
      ...formData,
      productId: scenario.productId,
      productName: scenario.productName,
      totalQuantity: scenario.totalQuantity,
      customer: scenario.customer,
      batchNo: `EXAMPLE-${Date.now()}`,
      originalDeliveryDate: scenario.originalDeliveryDate,
      requestedDeliveryDate: scenario.requestedDeliveryDate,
      reason: scenario.reason,
      priority: scenario.priority,
    });

    // 顯示載入成功訊息
    setShowExampleLoaded(true);
    setTimeout(() => setShowExampleLoaded(false), 2000);
  };

  // 分析資源衝突
  const analyzeConflicts = () => {
    const conflicts = {
      persons: [] as string[],
      machines: [] as string[],
      materials: [] as string[],
    };

    // 找出時間範圍內衝突的資源
    const requestedStart = new Date(formData.requestedDeliveryDate);
    const requestedEnd = new Date(requestedStart);
    requestedEnd.setMonth(requestedEnd.getMonth() + 1);

    mainWorkOrders.forEach((mwo) => {
      const mwoStart = new Date(mwo.plannedStart);
      const mwoEnd = new Date(mwo.plannedEnd);

      // 檢查時間是否重疊
      if (requestedStart < mwoEnd && requestedEnd > mwoStart) {
        const relatedSubOrders = subWorkOrders.filter((swo) => swo.mainOrderId === mwo.id);
        relatedSubOrders.forEach((swo) => {
          // 收集衝突的人員
          swo.assignedPersons.forEach((personId) => {
            if (!conflicts.persons.includes(personId)) {
              conflicts.persons.push(personId);
            }
          });
          // 收集衝突的機台
          if (swo.machineId && !conflicts.machines.includes(swo.machineId)) {
            conflicts.machines.push(swo.machineId);
          }
          // 收集衝突的物料
          swo.requiredMaterials.forEach((materialId) => {
            const material = materials.find((m) => m.id === materialId);
            if (material && material.stockQuantity <= material.safetyStock && !conflicts.materials.includes(materialId)) {
              conflicts.materials.push(materialId);
            }
          });
        });
      }
    });

    return conflicts;
  };

  const conflicts = analyzeConflicts();

  const getConflictingOrders = () => {
    const conflictingOrders: string[] = [];
    const requestedStart = new Date(formData.requestedDeliveryDate);
    const requestedEnd = new Date(requestedStart);
    requestedEnd.setMonth(requestedEnd.getMonth() + 1);

    mainWorkOrders.forEach((mwo) => {
      const mwoStart = new Date(mwo.plannedStart);
      const mwoEnd = new Date(mwo.plannedEnd);
      if (requestedStart < mwoEnd && requestedEnd > mwoStart) {
        conflictingOrders.push(mwo.id);
      }
    });

    return conflictingOrders;
  };

  /**
   * 處理表單提交 - 開始延後審核流程
   * Handle form submit - Start delay review flow
   */
  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();

    // 驗證所有欄位 | Validate all fields
    const newErrors: Record<string, string> = {};
    const fieldsToValidate = [
      'productId',
      'customer',
      'originalDeliveryDate',
      'requestedDeliveryDate',
      'reason',
      'totalQuantity',
    ] as const;

    fieldsToValidate.forEach((field) => {
      const error = validateField(field, formData[field]);
      if (error) newErrors[field] = error;
    });

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setTouched(new Set(fieldsToValidate));
      toast.error('請修正表單錯誤', '有些欄位需要修正');
      return;
    }

    // 1. 建立臨時插單物件（不含 ID，用於計算衝突）
    // Create temporary rush order object (without ID, for conflict calculation)
    const tempRushOrder: RushOrder = {
      id: `RUSH-${Date.now()}`, // 臨時 ID
      productId: formData.productId,
      productName: formData.productName,
      totalQuantity: formData.totalQuantity,
      customer: formData.customer,
      batchNo: formData.batchNo,
      orderDate: new Date(formData.orderDate).toISOString(),
      originalDeliveryDate: new Date(formData.originalDeliveryDate).toISOString(),
      requestedDeliveryDate: new Date(formData.requestedDeliveryDate).toISOString(),
      reason: formData.reason,
      priority: formData.priority,
      status: '待確認',
      conflictingOrders: getConflictingOrders(),
      resourceConflicts: conflicts,
      recommendedActions: generateRecommendations(),
      impactAssessment: generateImpactAssessment(),
      delayDecisions: [],
    };

    // 2. 計算衝突
    // Calculate conflicts
    const productsMap = new Map(products.map((p) => [p.id, p]));
    const subOrdersMap = new Map<string, typeof subWorkOrders>();

    mainWorkOrders.forEach((mwo) => {
      const relatedSubOrders = subWorkOrders.filter((swo) => swo.mainOrderId === mwo.id);
      subOrdersMap.set(mwo.id, relatedSubOrders);
    });

    const calculatedConflicts = calculateConflicts(
      tempRushOrder,
      mainWorkOrders,
      subOrdersMap,
      productsMap
    );

    // 3. 如果有衝突，開始審核流程；否則直接儲存
    // If conflicts exist, start review flow; otherwise save directly
    setIsSubmitting(true);
    await new Promise(resolve => setTimeout(resolve, 500)); // 視覺反饋

    try {
      if (calculatedConflicts.length > 0) {
        setPendingRushOrder(tempRushOrder);
        setConflictResults(calculatedConflicts);
        setCurrentConflictIndex(0);
        setDelayDecisions([]);
        setShowReviewDialog(true);
        setIsSubmitting(false);

        toast.warning(
          '發現資源衝突',
          `檢測到 ${calculatedConflicts.length} 個工單衝突，請進行審核`,
          4000
        );
      } else {
        // 無衝突，直接儲存
        // No conflicts, save directly
        addRushOrder(tempRushOrder);
        setIsSubmitting(false);
        toast.success(
          '插單建立成功',
          `插單 ${tempRushOrder.id} 已建立，無衝突工單`,
          3000
        );
        onClose();
      }
    } catch (error) {
      console.error('保存插單時發生錯誤:', error);
      setIsSubmitting(false);
      toast.error(
        '保存失敗',
        error instanceof Error ? error.message : '發生未預期的錯誤，請稍後再試',
        5000
      );
    }
  }, [formData, mainWorkOrders, subWorkOrders, products, addRushOrder, onClose, validateField]); // conflicts 已在函數內部重新計算

  const generateRecommendations = (): string[] => {
    const recommendations: string[] = [];

    if (conflicts.persons.length > 0) {
      recommendations.push('安排加班以增加人力產能');
    }
    if (conflicts.machines.length > 0) {
      recommendations.push('調整生產順序或增加機台產能');
    }
    if (conflicts.materials.length > 0) {
      recommendations.push('緊急採購不足的物料');
    }

    const conflictingOrders = getConflictingOrders();
    if (conflictingOrders.length > 0) {
      recommendations.push(`考慮延後以下訂單：${conflictingOrders.slice(0, 2).join(', ')}`);
    }

    return recommendations;
  };

  const generateImpactAssessment = () => {
    const conflictingOrders = getConflictingOrders();
    return conflictingOrders.map((orderId) => ({
      orderId,
      impactType: '延遲' as const,
      delayDays: 7,
      canPause: Math.random() > 0.5,
      notes: '需進行詳細評估',
    }));
  };

  const handleProductChange = (productId: string) => {
    const product = products.find((p) => p.id === productId);
    if (product) {
      handleChange('productId', productId);
      setFormData((prev) => ({
        ...prev,
        productId,
        productName: product.name,
      }));
    }
  };

  /**
   * 接受延後 - 記錄決策並移至下一個衝突
   * Accept delay - Record decision and move to next conflict
   */
  const handleAcceptDelay = useCallback(() => {
    if (!pendingRushOrder || conflictResults.length === 0) return;

    const currentConflict = conflictResults[currentConflictIndex];

    // 建立延後決策
    // Create delay decision
    const delayDecision = createDelayDecision(currentConflict, pendingRushOrder.id);

    // 記錄決策
    // Record decision
    const newDecisions = [...delayDecisions, delayDecision];
    setDelayDecisions(newDecisions);

    // 標記當前衝突為已審核並接受
    // Mark current conflict as reviewed and accepted
    const updatedConflicts = [...conflictResults];
    updatedConflicts[currentConflictIndex] = {
      ...currentConflict,
      isReviewed: true,
      isAccepted: true,
    };
    setConflictResults(updatedConflicts);

    // 重新計算剩餘衝突
    // Recalculate remaining conflicts
    const remainingConflicts = updatedConflicts.slice(currentConflictIndex + 1);
    const recalculatedConflicts = recalculateConflicts(
      remainingConflicts,
      delayDecision,
      mainWorkOrders
    );

    // 更新衝突結果：將重新計算的衝突合併回去
    // Update conflict results: merge recalculated conflicts back
    const newConflictResults = [
      ...updatedConflicts.slice(0, currentConflictIndex + 1),
      ...recalculatedConflicts
    ];
    setConflictResults(newConflictResults);

    // 移至下一個衝突
    // Move to next conflict
    if (currentConflictIndex + 1 < newConflictResults.length) {
      setCurrentConflictIndex(currentConflictIndex + 1);
    } else {
      // 所有衝突已審核完成
      // All conflicts reviewed
      handleCompleteReview(newDecisions);
    }
  }, [pendingRushOrder, conflictResults, currentConflictIndex, delayDecisions, mainWorkOrders]);

  /**
   * 完成審核 - 儲存插單及延後決策
   * Complete review - Save rush order and delay decisions
   */
  const handleCompleteReview = useCallback((finalDecisions: DelayDecision[]) => {
    if (!pendingRushOrder) return;

    try {
      // 儲存插單（包含延後決策）
      // Save rush order (with delay decisions)
      const finalRushOrder: RushOrder = {
        ...pendingRushOrder,
        delayDecisions: finalDecisions,
        conflictResults: conflictResults.map((c, idx) => ({
          ...c,
          isReviewed: idx <= currentConflictIndex,
          isAccepted: idx < currentConflictIndex || (idx === currentConflictIndex),
        })),
      };

      addRushOrder(finalRushOrder);

      // 顯示完成對話框
      // Show complete dialog
      setShowReviewDialog(false);
      setShowCompleteDialog(true);
    } catch (error) {
      console.error('保存插單時發生錯誤:', error);
      toast.error(
        '保存失敗',
        error instanceof Error ? error.message : '發生未預期的錯誤，請稍後再試',
        5000
      );
    }
  }, [pendingRushOrder, conflictResults, currentConflictIndex, addRushOrder]);

  /**
   * 關閉完成對話框
   * Close complete dialog
   */
  const handleCloseCompleteDialog = useCallback(() => {
    setShowCompleteDialog(false);
    onClose();
  }, [onClose]);

  /**
   * 關閉審核對話框（取消審核）
   * Close review dialog (cancel review)
   */
  const handleCloseReviewDialog = useCallback(() => {
    setShowReviewDialog(false);
    setConflictResults([]);
    setCurrentConflictIndex(0);
    setDelayDecisions([]);
    setPendingRushOrder(null);
  }, []);

  const getPersonName = (id: string) => persons.find((p) => p.id === id)?.name || id;
  const getMachineName = (id: string) => machines.find((m) => m.id === id)?.name || id;
  const getMaterialName = (id: string) => materials.find((m) => m.id === id)?.name || id;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fade-in">
      <div className="bg-gradient-to-br from-white to-slate-50 border-2 border-slate-200 rounded-2xl p-8 max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-gradient-to-br from-red-500 to-rose-500 rounded-xl flex items-center justify-center shadow-lg shadow-red-500/30">
            <span className="text-white text-lg">⚡</span>
          </div>
          <div>
            <h2 className="text-2xl font-bold text-slate-800">新增插單</h2>
            <p className="text-sm text-slate-500">緊急訂單插單處理</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* 產品選擇 */}
            <div>
              <label htmlFor="rushProductId" className="block text-sm font-medium text-[#475569] mb-1">
                產品 <span className="text-red-500">*</span>
              </label>
              <select
                id="rushProductId"
                ref={firstInputRef}
                required
                aria-required="true"
                value={formData.productId}
                onChange={(e) => handleProductChange(e.target.value)}
                onBlur={() => handleBlur('productId')}
                className={`input transition-colors ${errors.productId ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20' : ''}`}
                aria-invalid={!!errors.productId}
                aria-describedby={errors.productId ? 'rushProductId-error' : 'rushProductId-hint'}
              >
                <option value="">請選擇產品</option>
                {products.map((product) => (
                  <option key={product.id} value={product.id}>
                    {product.id} - {product.name}
                  </option>
                ))}
              </select>
              {errors.productId && (
                <p id="rushProductId-error" className="mt-1.5 text-sm text-red-600 flex items-center gap-1" role="alert">
                  <span aria-hidden="true">⚠️</span>
                  <span>{errors.productId}</span>
                </p>
              )}
            </div>

            {/* 客戶名稱 */}
            <div>
              <label htmlFor="rushCustomer" className="block text-sm font-medium text-[#475569] mb-1">
                客戶名稱 <span className="text-red-500">*</span>
              </label>
              <input
                id="rushCustomer"
                type="text"
                required
                aria-required="true"
                value={formData.customer}
                onChange={(e) => handleChange('customer', e.target.value)}
                onBlur={() => handleBlur('customer')}
                className={`input transition-colors ${errors.customer ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20' : ''}`}
                placeholder="輸入客戶名稱"
                aria-invalid={!!errors.customer}
                aria-describedby={errors.customer ? 'rushCustomer-error' : 'rushCustomer-hint'}
              />
              {errors.customer ? (
                <p id="rushCustomer-error" className="mt-1.5 text-sm text-red-600 flex items-center gap-1" role="alert">
                  <span aria-hidden="true">⚠️</span>
                  <span>{errors.customer}</span>
                </p>
              ) : (
                <p id="rushCustomer-hint" className="mt-1 text-xs text-slate-500">
                  請輸入完整的客戶公司或個人名稱
                </p>
              )}
            </div>

            {/* 數量 */}
            <div>
              <label htmlFor="rushQuantity" className="block text-sm font-medium text-[#475569] mb-1">
                數量 <span className="text-red-500">*</span>
              </label>
              <input
                id="rushQuantity"
                type="number"
                required
                aria-required="true"
                min="1"
                max="1000000"
                value={formData.totalQuantity}
                onChange={(e) => handleChange('totalQuantity', Number(e.target.value))}
                onBlur={() => handleBlur('totalQuantity')}
                className={`input transition-colors ${errors.totalQuantity ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20' : ''}`}
                aria-invalid={!!errors.totalQuantity}
                aria-describedby={errors.totalQuantity ? 'rushQuantity-error' : 'rushQuantity-hint'}
              />
              {errors.totalQuantity ? (
                <p id="rushQuantity-error" className="mt-1.5 text-sm text-red-600 flex items-center gap-1" role="alert">
                  <span aria-hidden="true">⚠️</span>
                  <span>{errors.totalQuantity}</span>
                </p>
              ) : (
                <p id="rushQuantity-hint" className="mt-1 text-xs text-slate-500">
                  插單數量（範圍：1 - 1,000,000）
                </p>
              )}
            </div>

            {/* 優先級 */}
            <div>
              <label htmlFor="rushPriority" className="block text-sm font-medium text-[#475569] mb-1">
                優先級 <span className="text-red-500">*</span>
              </label>
              <select
                id="rushPriority"
                required
                aria-required="true"
                value={formData.priority}
                onChange={(e) => setFormData({ ...formData, priority: e.target.value as RushOrder['priority'] })}
                className="input"
                aria-describedby="rushPriority-hint"
              >
                <option value="重要">重要</option>
                <option value="緊急">緊急</option>
              </select>
              <p id="rushPriority-hint" className="mt-1 text-xs text-slate-500">
                選擇「緊急」會觸發更詳細的衝突分析
              </p>
            </div>

            {/* 原交期 */}
            <div>
              <label htmlFor="rushOriginalDate" className="block text-sm font-medium text-[#475569] mb-1">
                原定交期 <span className="text-red-500">*</span>
              </label>
              <input
                id="rushOriginalDate"
                type="date"
                required
                aria-required="true"
                value={formData.originalDeliveryDate}
                onChange={(e) => handleChange('originalDeliveryDate', e.target.value)}
                onBlur={() => handleBlur('originalDeliveryDate')}
                className={`input transition-colors ${errors.originalDeliveryDate ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20' : ''}`}
                aria-invalid={!!errors.originalDeliveryDate}
                aria-describedby={errors.originalDeliveryDate ? 'rushOriginalDate-error' : 'rushOriginalDate-hint'}
              />
              {errors.originalDeliveryDate ? (
                <p id="rushOriginalDate-error" className="mt-1.5 text-sm text-red-600 flex items-center gap-1" role="alert">
                  <span aria-hidden="true">⚠️</span>
                  <span>{errors.originalDeliveryDate}</span>
                </p>
              ) : (
                <p id="rushOriginalDate-hint" className="mt-1 text-xs text-slate-500">
                  原計畫的交付日期
                </p>
              )}
            </div>

            {/* 要求交期 */}
            <div>
              <label htmlFor="rushRequestedDate" className="block text-sm font-medium text-[#475569] mb-1">
                要求交期 <span className="text-red-500">*</span>
              </label>
              <input
                id="rushRequestedDate"
                type="date"
                required
                aria-required="true"
                value={formData.requestedDeliveryDate}
                onChange={(e) => handleChange('requestedDeliveryDate', e.target.value)}
                onBlur={() => handleBlur('requestedDeliveryDate')}
                className={`input transition-colors ${errors.requestedDeliveryDate ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20' : ''}`}
                aria-invalid={!!errors.requestedDeliveryDate}
                aria-describedby={errors.requestedDeliveryDate ? 'rushRequestedDate-error' : 'rushRequestedDate-hint'}
              />
              {errors.requestedDeliveryDate ? (
                <p id="rushRequestedDate-error" className="mt-1.5 text-sm text-red-600 flex items-center gap-1" role="alert">
                  <span aria-hidden="true">⚠️</span>
                  <span>{errors.requestedDeliveryDate}</span>
                </p>
              ) : (
                <p id="rushRequestedDate-hint" className="mt-1 text-xs text-slate-500">
                  希望提前完成的日期，系統將分析資源衝突
                </p>
              )}
            </div>
          </div>

          {/* 插單原因 */}
          <div>
            <label htmlFor="rushReason" className="block text-sm font-medium text-[#475569] mb-1">
              插單原因 <span className="text-red-500">*</span>
            </label>
            <textarea
              id="rushReason"
              required
              aria-required="true"
              value={formData.reason}
              onChange={(e) => handleChange('reason', e.target.value)}
              onBlur={() => handleBlur('reason')}
              className={`input transition-colors ${errors.reason ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20' : ''}`}
              rows={2}
              placeholder="請說明插單原因"
              aria-invalid={!!errors.reason}
              aria-describedby={errors.reason ? 'rushReason-error' : 'rushReason-hint'}
            />
            {errors.reason ? (
              <p id="rushReason-error" className="mt-1.5 text-sm text-red-600 flex items-center gap-1" role="alert">
                <span aria-hidden="true">⚠️</span>
                <span>{errors.reason}</span>
              </p>
            ) : (
              <p id="rushReason-hint" className="mt-1 text-xs text-slate-500">
                詳細說明插單原因（至少 10 個字元），系統將根據原因進行衝突分析
              </p>
            )}
          </div>

          {/* 衝突分析顯示 */}
          {formData.requestedDeliveryDate && (
            <div className="mt-6 p-5 bg-gradient-to-br from-red-50 to-rose-50 border-2 border-red-200 rounded-xl shadow-sm">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 bg-gradient-to-br from-red-500 to-rose-500 rounded-lg flex items-center justify-center shadow-md shadow-red-500/30">
                  <span className="text-white text-sm">⚠️</span>
                </div>
                <h3 className="text-lg font-bold text-red-800">資源衝突分析</h3>
              </div>

              <div className="space-y-3">
                {/* 衝突人員 */}
                {conflicts.persons.length > 0 && (
                  <div className="bg-white/70 border border-red-200 rounded-lg p-3">
                    <p className="text-sm font-bold text-red-700 mb-2">👥 衝突人員</p>
                    <div className="flex flex-wrap gap-2">
                      {conflicts.persons.map((personId) => (
                        <span
                          key={personId}
                          className="px-3 py-1.5 bg-gradient-to-r from-red-100 to-rose-100 text-red-800 rounded-lg text-xs font-semibold border border-red-200 shadow-sm"
                        >
                          {getPersonName(personId)}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* 衝突機台 */}
                {conflicts.machines.length > 0 && (
                  <div className="bg-white/70 border border-red-200 rounded-lg p-3">
                    <p className="text-sm font-bold text-red-700 mb-2">🔧 衝突機台</p>
                    <div className="flex flex-wrap gap-2">
                      {conflicts.machines.map((machineId) => (
                        <span
                          key={machineId}
                          className="px-3 py-1.5 bg-gradient-to-r from-red-100 to-rose-100 text-red-800 rounded-lg text-xs font-semibold border border-red-200 shadow-sm"
                        >
                          {getMachineName(machineId)}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* 衝突物料 */}
                {conflicts.materials.length > 0 && (
                  <div className="bg-white/70 border border-red-200 rounded-lg p-3">
                    <p className="text-sm font-bold text-red-700 mb-2">📦 庫存不足物料</p>
                    <div className="flex flex-wrap gap-2">
                      {conflicts.materials.map((materialId) => (
                        <span
                          key={materialId}
                          className="px-3 py-1.5 bg-gradient-to-r from-red-100 to-rose-100 text-red-800 rounded-lg text-xs font-semibold border border-red-200 shadow-sm"
                        >
                          {getMaterialName(materialId)}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* 衝突訂單 */}
                {getConflictingOrders().length > 0 && (
                  <div className="bg-white/70 border border-orange-200 rounded-lg p-3">
                    <p className="text-sm font-bold text-orange-700 mb-2">📋 衝突工單</p>
                    <div className="flex flex-wrap gap-2">
                      {getConflictingOrders().map((orderId) => (
                        <span
                          key={orderId}
                          className="px-3 py-1.5 bg-gradient-to-r from-orange-100 to-amber-100 text-orange-800 rounded-lg text-xs font-semibold border border-orange-200 shadow-sm"
                        >
                          {orderId}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* 建議方案 */}
                {generateRecommendations().length > 0 && (
                  <div className="bg-white/70 border border-blue-200 rounded-lg p-3">
                    <p className="text-sm font-bold text-blue-700 mb-2">💡 建議應對方案</p>
                    <ul className="mt-2 space-y-1.5">
                      {generateRecommendations().map((rec, index) => (
                        <li key={index} className="text-sm text-blue-700 flex items-start gap-2">
                          <span className="text-blue-500 font-bold">•</span>
                          <span>{rec}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* 按鈕 */}
          <div className="flex justify-between items-center pt-6 border-t-2 border-slate-200">
            {/* 左側：載入範例按鈕 */}
            <div className="flex flex-wrap gap-2">
              {/* 重要範例下拉選單 */}
              <select
                onChange={(e) => {
                  const index = parseInt(e.target.value);
                  if (!isNaN(index)) {
                    handleLoadImportantExample(index);
                    e.target.value = ''; // Reset select
                  }
                }}
                className="px-4 py-2.5 bg-gradient-to-r from-amber-50 to-yellow-50 border-2 border-amber-300 text-amber-800 rounded-xl text-sm font-semibold cursor-pointer hover:from-amber-100 hover:to-yellow-100 transition-all shadow-sm hover:shadow-md focus:ring-2 focus:ring-amber-400"
                defaultValue=""
              >
                <option value="" disabled>
                  ▸ 載入重要範例
                </option>
                {importantScenarios.map((scenario, idx) => (
                  <option key={idx} value={idx}>
                    {scenario.name}
                  </option>
                ))}
              </select>

              {/* 緊急範例下拉選單 */}
              <select
                onChange={(e) => {
                  const index = parseInt(e.target.value);
                  if (!isNaN(index)) {
                    handleLoadUrgentExample(index);
                    e.target.value = ''; // Reset select
                  }
                }}
                className="px-4 py-2.5 bg-gradient-to-r from-red-50 to-rose-50 border-2 border-red-300 text-red-800 rounded-xl text-sm font-semibold cursor-pointer hover:from-red-100 hover:to-rose-100 transition-all shadow-sm hover:shadow-md focus:ring-2 focus:ring-red-400"
                defaultValue=""
              >
                <option value="" disabled>
                  ▸ 載入緊急範例
                </option>
                {urgentScenarios.map((scenario, idx) => (
                  <option key={idx} value={idx}>
                    {scenario.name}
                  </option>
                ))}
              </select>
            </div>

            {/* 右側：取消與確認按鈕 */}
            <div className="flex gap-3">
              {showExampleLoaded && (
                <span className="px-4 py-2.5 bg-gradient-to-r from-green-100 to-emerald-100 text-green-800 rounded-xl text-sm font-semibold border border-green-300 flex items-center shadow-sm">
                  ✓ 範例已載入
                </span>
              )}
              <button
                type="button"
                onClick={onClose}
                className="px-6 py-2.5 bg-gradient-to-r from-slate-100 to-slate-200 text-slate-700 rounded-xl hover:from-slate-200 hover:to-slate-300 transition-all text-sm font-semibold shadow-sm hover:shadow-md active:scale-95"
              >
                取消
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-6 py-2.5 bg-gradient-to-r from-red-600 to-rose-500 text-white rounded-xl hover:from-red-700 hover:to-rose-600 transition-all text-sm font-semibold shadow-lg shadow-red-500/30 hover:shadow-xl hover:shadow-red-500/40 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isSubmitting ? (
                  <>
                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                    <span>處理中...</span>
                  </>
                ) : (
                  <>⚡ 確認插單</>
                )}
              </button>
            </div>
          </div>
        </form>

        {/* 延後審核對話框 | Delay Review Dialog */}
        {showReviewDialog && conflictResults.length > 0 && (
          <DelayReviewDialog
            conflict={conflictResults[currentConflictIndex]}
            currentIndex={currentConflictIndex}
            total={conflictResults.length}
            onAccept={handleAcceptDelay}
            onClose={handleCloseReviewDialog}
          />
        )}

        {/* 審核完成對話框 | Review Complete Dialog */}
        {showCompleteDialog && (
          <ReviewCompleteDialog
            acceptedCount={delayDecisions.length}
            totalConflicts={conflictResults.length}
            onClose={handleCloseCompleteDialog}
          />
        )}
      </div>
    </div>
  );
}
